import { ChangeEventHandler, MouseEvent, useCallback, useEffect } from 'react';
import { TblPagination } from './table.types';
import { getPaginationLogic } from './utils/getPaginationLogic';

const PAGE_BATCH_SIZE = 5;
export const PAGE_SIZE_OPTIONS: number[] = [20, 50, 100, 250, 500, 1000];

type UseTablePagingReturn = {
    pageNumbersVisible: number[];
    showNavToFirst: boolean;
    showNavToLast: boolean;
    showNavToPrevBatch: boolean;
    showNavToNextBatch: boolean;
    handleNavToNextBatch: (event: MouseEvent<HTMLButtonElement>) => void;
    handleNavToPrevBatch: (event: MouseEvent<HTMLButtonElement>) => void;
    handleNavToPage: (e: MouseEvent<HTMLSpanElement>, page: number) => void;
    handleChangePageSize: ChangeEventHandler<HTMLSelectElement>;
};
type UseTablePagingProps = {
    paging: TblPagination;
    setPaging: (pg: Partial<TblPagination>) => void;
};

const useTablePaging = ({ paging, setPaging }: UseTablePagingProps): UseTablePagingReturn => {
    const {
        pagesByBatch,
        batchIndex,
        pageNumbersVisible,
        showNavToFirst,
        showNavToLast,
        showNavToPrevBatch,
        showNavToNextBatch,
    } = getPaginationLogic(paging, PAGE_BATCH_SIZE);

    useEffect(() => {
        /**
         * Skips on first render, and only runs if filtering produces fewer
         * total pages than the current page.
         * This is to prevent the user from being stuck on a page that doesn't exist
         * after filtering.
         * */
        if (paging.total !== 0 && paging.page > paging.total) {
            setPaging({ page: paging.total });
        }
    }, [paging, setPaging]);

    const handleNavToNextBatch = useCallback(
        (event: MouseEvent<HTMLButtonElement>): void => {
            event.preventDefault();

            const nextBatchIdx = batchIndex + 1;
            const page = pagesByBatch[nextBatchIdx][0];
            setPaging({ page });
        },
        [batchIndex, pagesByBatch, setPaging],
    );

    const handleNavToPrevBatch = useCallback(
        (event: MouseEvent<HTMLButtonElement>): void => {
            event.preventDefault();

            const nextBatchIdx = batchIndex - 1;
            const batch = pagesByBatch[nextBatchIdx];
            const page = batch[batch.length - 1];
            setPaging({ page });
        },
        [batchIndex, pagesByBatch, setPaging],
    );

    const handleNavToPage = useCallback(
        (e: MouseEvent<HTMLSpanElement>, page: number): void => {
            e.preventDefault();
            setPaging({ page });
        },
        [setPaging],
    );

    const handleChangePageSize: ChangeEventHandler<HTMLSelectElement> = useCallback(
        e => {
            e.preventDefault();
            const size = parseInt(e.target.value);

            if (size > 0 && size != paging.size) {
                setPaging({ size, page: 1 });
            }
        },
        [paging.size, setPaging],
    );

    /**
     * Adapted from existing Angular Pagination
     *
     * According to predecessor, we have 4 types of page navigation buttons, excluding the PAGE SIZE selector.
     * We never show more than 5 "middle pages" at a time.
     * There exist forward and backward buttons to flip through the "batches" of pages.
     *
     * Like so [1,2,3,4,5] << [6,7,8,9,10] >> [11,12,13,14,15]
     * Where the first and last lists are the previous and next batches.
     * */

    /**
     * Types of Page Buttons
     * -- [nav to first page]
     * -- [middle pages (5 or less)]
     * -- [next middle pages]
     * -- [nav to last page]
     */

    /**
     * SHOW ALL BUTTONS EXCEPT NAV FIRST WHEN
     * -- greater than 5 pages
     * -- current middle pages batch is index 2, and no lesser
     */

    /**
     * SHOW ALL BUTTONS EXCEPT NAV LAST WHEN
     * -- greater than 5 pages
     * -- current middle pages batch is the third to last one (last index - 3), and no greater
     */

    /**
     * SHOW ALL BUTTONS WHEN
     * -- greater than 5 pages
     * -- at least 3 batches of 5 pages exist
     * -- current middle pages batch is at least the second AND less then the second to last
     */

    /**
     * SHOW ONLY MIDDLE PAGE(S) WHEN
     * -- fewer than 6 pages
     */

    return {
        pageNumbersVisible,
        showNavToFirst,
        showNavToLast,
        showNavToPrevBatch,
        showNavToNextBatch,
        handleNavToNextBatch,
        handleNavToPrevBatch,
        handleNavToPage,
        handleChangePageSize,
    };
};

export default useTablePaging;
